<script setup lang="ts">
import { computed, ref } from 'vue'
import { VueEditor } from 'vue2-editor'
import { mdiLeadPencil, mdiUpload } from '@mdi/js'

import BKUserSelect from '@/components/BKUserSelect.vue'
import BKOrganizationSelect from '@/components/BKOrganizationSelect.vue'
import GmapsPlaceInput from '@/components/GmapsPlaceInput.vue'
import EventEditActionButtons from '@/pages/CreateEventProcessingPage/ui/layout/EventEditActionButtons.vue'
import { eventApi, IEventStaffRole, IEventEdit } from '@/enitites/event'
import { UserDB } from '@/enitites/user'
import { useImage } from '@/shared/composables/image'
import BKEditorConfig from '@/common/vue2Config'

// @ts-expect-error Not a ts file
import store from '@/store'
import { useEventUpdateMutation } from '@/enitites/event/api/queries'

const { getImageById, saveImage } = useImage()
const { error, isPending, mutate: updateEvent } = useEventUpdateMutation()

const props = defineProps<{
  eventData: IEventEdit
}>()

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'back'): void
  (e: 'cancel'): void
}>()

const image = ref(props.eventData.image)
const orgId = ref(props.eventData.orgId)
const title = ref(props.eventData.title)
const location = ref({
  address: props.eventData.locationAddress,
  lat: props.eventData.locationLat,
  lng: props.eventData.locationLng,
})
const website = ref(props.eventData.website)
const description = ref(props.eventData.description)
const staff = ref(props.eventData.staff)

const errorMessage = ref<string>()
const isSavingImage = ref(false)
const imageError = ref<string>()
const optimisticImage = ref<string>()
const inputFileRef = ref<HTMLInputElement | null>(null)

const getUserDB = computed(() => store.getters.getUserDB)
const imageSrc = computed(() => {
  if (optimisticImage.value) {
    return optimisticImage.value
  }
  return image.value ? getImageById(image.value, 800, 800, 'event') : ''
})
const isInfoFilledOut = computed(() => !!title.value)
const adminIds = computed(() => staff.value.admins.map((user) => user.id))
const djIds = computed(() => staff.value.djs.map((user) => user.id))
const hostIds = computed(() => staff.value.hosts.map((user) => user.id))

function isValidUrl(str?: string): boolean {
  if (!str) return true
  try {
    const url = new URL(str)
    return url.protocol === 'https:'
  } catch {
    return false
  }
}

const validateUrlRule = (value: string) => isValidUrl(value) || 'URL should be in the format https://yourwebsite.com'

function openFileDialog(): void {
  inputFileRef.value?.click()
}

async function saveImageToEvent(imageFile: File): Promise<void> {
  isSavingImage.value = true
  imageError.value = undefined

  try {
    const result = await saveImage(imageFile)
    if (result) {
      image.value = result
    } else {
      optimisticImage.value = undefined
      imageError.value = 'Failed to upload image'
    }
  } catch (e) {
    optimisticImage.value = undefined
    imageError.value = (e as Error).message || 'Failed to upload image'
  } finally {
    isSavingImage.value = false
  }
}

function handleFileChange(evt: Event): void {
  const filesImg = (evt.target as HTMLInputElement).files
  if (!filesImg?.length) return

  saveImageToEvent(filesImg[0])
  const reader = new FileReader()
  reader.onload = (event) => {
    optimisticImage.value = event.target?.result as string
  }
  reader.readAsDataURL(filesImg[0])
}

async function addEventRole(user: UserDB, role: IEventStaffRole): Promise<void> {
  try {
    const resp = await eventApi.addRole(props.eventData.id, user.id, role)
    if (resp) staff.value = resp
  } catch (e) {
    errorMessage.value = (e as Error).message || 'Failed to add role'
  }
}

async function removeEventRole(user: UserDB, role: IEventStaffRole): Promise<void> {
  try {
    const resp = await eventApi.removeRole(props.eventData.id, user.id, role)
    if (resp) staff.value = resp
  } catch (e) {
    errorMessage.value = (e as Error).message || 'Failed to remove role'
  }
}

function saveEventData(onSuccess: () => void) {
  if (!isInfoFilledOut.value || isPending.value) return
  errorMessage.value = undefined

  const payload = {
    ...props.eventData,
    id: props.eventData.id,
    image: image.value,
    orgId: orgId.value,
    title: title.value,
    locationAddress: location.value.address,
    locationLat: location.value.lat,
    locationLng: location.value.lng,
    website: website.value,
    description: description.value,
    staff: staff.value,
  }

  updateEvent(payload, { onSuccess })
}

async function handleNext() {
  saveEventData(() => {
    emit('next')
  })
}

function handleCancel() {
  saveEventData(() => {
    emit('cancel')
  })
}
</script>

<template>
  <div class="information pb-10">
    <v-img
      :src="imageSrc"
      class="information__image d-flex align-center"
      :style="{ opacity: isSavingImage ? 0.5 : 1 }"
    >
      <v-btn
        v-if="!imageSrc || isSavingImage"
        :loading="isSavingImage"
        class="information__btn-upload"
        color="var(--color-white)"
        outlined
        tile
        @click="openFileDialog"
      >
        Upload image
        <v-icon class="ml-4">
          {{ mdiUpload }}
        </v-icon>
      </v-btn>
      <v-btn
        v-else
        class="information__btn-edit white--text"
        color="var(--color-blue-800)"
        tile
        @click="openFileDialog"
      >
        Edit Image
        <v-icon class="ml-4">
          {{ mdiLeadPencil }}
        </v-icon>
      </v-btn>
      <input
        ref="inputFileRef"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        name="eventImage"
        aria-label="Upload event image"
        style="display: none"
        type="file"
        @change="handleFileChange"
      />
    </v-img>

    <p
      v-if="imageError"
      class="error--text mt-2"
    >
      {{ imageError }}
    </p>

    <p class="information__title">General information *</p>

    <BKOrganizationSelect
      :orgs="getUserDB.organizations"
      :selected-org.sync="orgId"
      class="mb-3"
    />

    <v-text-field
      v-model="title"
      autocomplete="off"
      background-color="#fff"
      class="information__field"
      hide-details="auto"
      label="Event name"
      maxlength="150"
      outlined
    />

    <GmapsPlaceInput
      v-model="location"
      class="information__location"
      placeholder="Location"
    />

    <v-text-field
      v-model="website"
      :rules="[validateUrlRule]"
      autocomplete="off"
      background-color="#fff"
      class="information__field"
      hide-details="auto"
      label="Website (optional)"
      outlined
    />

    <VueEditor
      v-model="description"
      :editor-toolbar="BKEditorConfig"
      placeholder="Give a short description of the event."
    />

    <p class="information__title">Add Admins</p>
    <!--      eslint-disable vuejs-accessibility/aria-role-->
    <BKUserSelect
      :exclude="adminIds"
      :select-user-chip.sync="staff.admins"
      label-text="Find and add Admins"
      role="admin"
      @removeUser="removeEventRole"
      @selectUser="addEventRole"
    />

    <p class="information__title">Add Host</p>
    <BKUserSelect
      :exclude="hostIds"
      :select-user-chip.sync="staff.hosts"
      label-text="Find and add Host"
      role="host"
      @removeUser="removeEventRole"
      @selectUser="addEventRole"
    />

    <p class="information__title">Add DJs</p>
    <p class="information__title information__title--description">
      You can also add DJs to individual battles when you create the battles
    </p>
    <BKUserSelect
      :exclude="djIds"
      :select-user-chip.sync="staff.djs"
      label-text="Find and add Djs"
      role="dj"
      @removeUser="removeEventRole"
      @selectUser="addEventRole"
    />
    <!--      eslint-enable vuejs-accessibility/aria-role-->

    <EventEditActionButtons
      :is-loading="isPending"
      :disabled="!isInfoFilledOut"
      :error="errorMessage || error?.message"
      @next="handleNext"
      @back="$emit('back')"
      @cancel="handleCancel"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.information {
  max-width: 400px;
  margin: 0 auto;

  &__image {
    position: relative;
    width: 100%;
    height: 400px;
    background: #6cace4;

    @media (max-width: $screen-sm) {
      height: 320px;
    }
  }

  &__btn-upload {
    margin-left: 25%;
  }

  &__btn-edit {
    position: absolute;
    bottom: 10px;
    left: 10px;
    font-weight: var(--font-weight-bold);
  }

  .error--text {
    font-size: 14px;
    text-align: center;
    color: red;
  }

  &__title {
    margin: 12px 0;
    color: var(--color-dark-800);

    &--description {
      color: #999;
    }
  }

  &__field {
    width: 100%;
    margin-bottom: 10px !important;
  }

  &__location {
    width: 100%;
  }
}
</style>
